import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../api/api";
import AdminLayout from "../../components/admin/AdminLayout";

export default function TournamentRules() {
  const { id } = useParams();

  const [tournament, setTournament] = useState(null);
  const [rules, setRules] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const [sectionTitle, setSectionTitle] = useState("");
  const [content, setContent] = useState("");

  // =========================================================
  // LOAD RULES
  // =========================================================

  async function loadRules() {
    try {
      setLoading(true);

      const data = await API.get(`/tournaments/${id}/rules`);

      setTournament(data.tournament);
      setRules(Array.isArray(data.rules) ? data.rules : []);
    } catch (err) {
      console.error("Failed to load tournament rules:", err);
      alert(err.message || "Failed to load tournament rules");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      loadRules();
    }
  }, [id]);

  // =========================================================
  // RESET FORM
  // =========================================================

  function resetForm() {
    setSectionTitle("");
    setContent("");
    setEditingId(null);
    setShowForm(false);
  }

  // =========================================================
  // ADD RULE
  // =========================================================

  function startAddRule() {
    setSectionTitle("");
    setContent("");
    setEditingId(null);
    setShowForm(true);
  }

  // =========================================================
  // EDIT RULE
  // =========================================================

  function startEditRule(rule) {
    setEditingId(rule.id);
    setSectionTitle(rule.section_title);
    setContent(rule.content);
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  // =========================================================
  // SAVE RULE
  // =========================================================

  async function handleSaveRule(e) {
    e.preventDefault();

    if (!sectionTitle.trim()) {
      alert("Please enter a section title.");
      return;
    }

    if (!content.trim()) {
      alert("Please enter the rule content.");
      return;
    }

    try {
      setSaving(true);

      if (editingId) {
        await API.put(
          `/tournaments/${id}/rules/${editingId}`,
          {
            section_title: sectionTitle,
            content,
          }
        );
      } else {
        await API.post(
          `/tournaments/${id}/rules`,
          {
            section_title: sectionTitle,
            content,
          }
        );
      }

      resetForm();

      await loadRules();
    } catch (err) {
      console.error("Save rule failed:", err);
      alert(err.message || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  }

  // =========================================================
  // DELETE RULE
  // =========================================================

  async function handleDeleteRule(rule) {
    const confirmed = window.confirm(
      `Delete "${rule.section_title}"?`
    );

    if (!confirmed) return;

    try {
      await API.delete(
        `/tournaments/${id}/rules/${rule.id}`
      );

      setRules((prev) =>
        prev.filter((item) => item.id !== rule.id)
      );
    } catch (err) {
      console.error("Delete rule failed:", err);
      alert(err.message || "Failed to delete rule");
    }
  }

  // =========================================================
  // MOVE RULE
  // =========================================================

  async function moveRule(index, direction) {
    const newIndex = index + direction;

    if (
      newIndex < 0 ||
      newIndex >= rules.length
    ) {
      return;
    }

    const updated = [...rules];

    const temp = updated[index];

    updated[index] = updated[newIndex];
    updated[newIndex] = temp;

    // Update sort order locally
    const reordered = updated.map((rule, i) => ({
      ...rule,
      sort_order: i,
    }));

    setRules(reordered);

    try {
      await API.put(
        `/tournaments/${id}/rules/reorder`,
        {
          rules: reordered.map((rule, i) => ({
            id: rule.id,
            sort_order: i,
          })),
        }
      );
    } catch (err) {
      console.error("Reorder failed:", err);

      alert(
        err.message ||
          "Failed to save rule order"
      );

      loadRules();
    }
  }

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-black text-white p-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-gray-400">
              Loading fight rules...
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <AdminLayout>
      <div className="min-h-screen bg-black text-white p-6 md:p-8">

        <div className="max-w-5xl mx-auto">

          {/* ================================================= */}
          {/* HEADER */}
          {/* ================================================= */}

          <div className="mb-8">

            <div className="text-sm uppercase tracking-widest text-[#d4af37] mb-2">
              Tournament Management
            </div>

            <h1 className="text-3xl md:text-4xl font-black">
              Fight Rules
            </h1>

            {tournament && (
              <p className="text-gray-400 mt-2 text-lg">
                {tournament.name}
              </p>
            )}

          </div>

          {/* ================================================= */}
          {/* ADD / EDIT FORM */}
          {/* ================================================= */}

          {showForm && (
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 mb-8 shadow-xl">

              <div className="flex justify-between items-center mb-6">

                <h2 className="text-xl font-bold text-[#d4af37]">
                  {editingId
                    ? "Edit Rule Section"
                    : "Add Rule Section"}
                </h2>

                <button
                  type="button"
                  onClick={resetForm}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ×
                </button>

              </div>

              <form
                onSubmit={handleSaveRule}
                className="space-y-5"
              >

                {/* TITLE */}

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Section Title
                  </label>

                  <input
                    type="text"
                    value={sectionTitle}
                    onChange={(e) =>
                      setSectionTitle(e.target.value)
                    }
                    placeholder="e.g. ALLOWED TECHNIQUES"
                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#d4af37]"
                  />
                </div>

                {/* CONTENT */}

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Rule Content
                  </label>

                  <textarea
                    value={content}
                    onChange={(e) =>
                      setContent(e.target.value)
                    }
                    rows={8}
                    placeholder="Enter the rules for this section..."
                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#d4af37] resize-y"
                  />
                </div>

                {/* ACTIONS */}

                <div className="flex gap-3">

                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-[#d4af37] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e5c158] disabled:opacity-50"
                  >
                    {saving
                      ? "Saving..."
                      : editingId
                      ? "Save Changes"
                      : "Add Rule"}
                  </button>

                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-800 text-white px-6 py-3 rounded-xl hover:bg-gray-700"
                  >
                    Cancel
                  </button>

                </div>

              </form>

            </div>
          )}

          {/* ================================================= */}
          {/* TOOLBAR */}
          {/* ================================================= */}

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-5">

            <div>
              <h2 className="text-xl font-bold">
                Rule Sections
              </h2>

              <p className="text-gray-500 text-sm">
                {rules.length} section
                {rules.length !== 1 ? "s" : ""}
              </p>
            </div>

            {!showForm && (
              <button
                onClick={startAddRule}
                className="bg-[#d4af37] text-black font-bold px-5 py-3 rounded-xl hover:bg-[#e5c158] transition"
              >
                + Add Rule Section
              </button>
            )}

          </div>

          {/* ================================================= */}
          {/* EMPTY */}
          {/* ================================================= */}

          {rules.length === 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-10 text-center">

              <div className="text-5xl mb-4">
                📋
              </div>

              <h3 className="text-xl font-bold mb-2">
                No Fight Rules Yet
              </h3>

              <p className="text-gray-500 mb-6">
                Add the rules fighters must follow
                for this tournament.
              </p>

              {!showForm && (
                <button
                  onClick={startAddRule}
                  className="bg-[#d4af37] text-black font-bold px-6 py-3 rounded-xl"
                >
                  Create First Rule
                </button>
              )}

            </div>
          )}

          {/* ================================================= */}
          {/* RULE LIST */}
          {/* ================================================= */}

          <div className="space-y-4">

            {rules.map((rule, index) => (

              <div
                key={rule.id}
                className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden"
              >

                {/* TOP */}

                <div className="flex items-center gap-3 p-4 border-b border-gray-800">

                  {/* ORDER */}

                  <div className="flex flex-col gap-1">

                    <button
                      onClick={() =>
                        moveRule(index, -1)
                      }
                      disabled={index === 0}
                      className="w-8 h-7 bg-gray-800 rounded text-gray-300 hover:bg-gray-700 disabled:opacity-20"
                      title="Move up"
                    >
                      ↑
                    </button>

                    <button
                      onClick={() =>
                        moveRule(index, 1)
                      }
                      disabled={
                        index === rules.length - 1
                      }
                      className="w-8 h-7 bg-gray-800 rounded text-gray-300 hover:bg-gray-700 disabled:opacity-20"
                      title="Move down"
                    >
                      ↓
                    </button>

                  </div>

                  {/* NUMBER */}

                  <div className="w-10 h-10 rounded-full bg-[#d4af37] text-black flex items-center justify-center font-black">
                    {index + 1}
                  </div>

                  {/* TITLE */}

                  <div className="flex-1 min-w-0">

                    <h3 className="font-bold text-lg truncate">
                      {rule.section_title}
                    </h3>

                  </div>

                  {/* ACTIONS */}

                  <div className="flex gap-2">

                    <button
                      onClick={() =>
                        startEditRule(rule)
                      }
                      className="px-3 py-2 bg-gray-800 rounded-lg hover:bg-gray-700 text-sm"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() =>
                        handleDeleteRule(rule)
                      }
                      className="px-3 py-2 bg-red-900/40 text-red-400 rounded-lg hover:bg-red-900/60 text-sm"
                    >
                      Delete
                    </button>

                  </div>

                </div>

                {/* CONTENT PREVIEW */}

                <div className="p-5">

                  <div className="text-gray-300 whitespace-pre-wrap leading-relaxed text-sm">
                    {rule.content}
                  </div>

                </div>

              </div>

            ))}

          </div>

        </div>

      </div>
    </AdminLayout>
  );
}
